import React, {useState, useEffect, useMemo} from 'react';
import {
    Box, Dialog, Divider, TextField, Typography, IconButton,
    Slide, Button, MenuItem, Select, Stack, ToggleButtonGroup, ToggleButton
} from "@mui/material";
import usePA_GlobalStore from "../../../Services/ZustandGlobalStore";
import {capitalizeFirstLetter} from "../../Helpers/TextStyling";

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

function SuppliesEditItemDialog({ open, onClose, item, onSave, onDelete }) {

    const { PA_GlobalObject } = usePA_GlobalStore();

    const [editData, setEditData] = useState(null);

    useEffect(() => {
        if (item) {
            setEditData({
                ...item,
                sub_category: item.sub_category || "General"
            });
        }
    }, [item, open]);

    const dynamicSubCats = useMemo(() => {
        const currentSupplies = PA_GlobalObject?.Planning?.supplies || [];
        const defaults = ["General", "Cooking", "Shelter", "Tools", "Hygiene"];

        // Filter supplies by the current category being edited
        const existing = currentSupplies
            .filter(s => s.category === (editData?.category || item?.category))
            .map(s => s.sub_category?.trim());

        const combined = new Set([...defaults]);
        existing.forEach(sub => {
            if (!sub) return;
            const exists = Array.from(combined).some(
                ext => ext.toLowerCase() === sub.toLowerCase()
            );
            if (!exists) combined.add(sub);
        });

        return Array.from(combined).sort();
    }, [PA_GlobalObject?.Planning?.supplies, editData?.category, item?.category]);

    if (!editData) return null;


    // Helper to determine if the current sub_category is a custom entry
    const isCustom = editData.sub_category === "" || !dynamicSubCats.includes(editData.sub_category);

    // Check if the item is currently assigned to a user (assuming "0" is Bounty)
    const isAssigned = editData.assigned_to !== "0" && editData.assigned_to !== "";

    const handleReleaseToBounty = () => {
        // 1. Create the updated object manually to bypass state delay
        const updatedItem = {
            ...editData,
            assigned_to: "0"
        };

        // 2. Trigger the save immediately
        onSave(updatedItem);

        // 3. Close the dialog
        onClose();
    };



    return (
        <Dialog
            open={open}
            onClose={onClose}
            TransitionComponent={Transition}
            maxWidth="sm"
            PaperProps={{
                sx: { borderRadius: '20px', backgroundColor: '#fff', overflow: 'hidden' }
            }}
        >
            {/* COMMON HEADER */}
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f3f5' }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#151269' }}>Edit Item</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#95a5a6', letterSpacing: 0.5 }}>MODIFY LOGISTICS</Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: '#e74c3c', backgroundColor: 'rgba(231, 76, 60, 0.1)', '&:hover': { backgroundColor: 'rgba(231, 76, 60, 0.2)' } }}>
                    <i className="icon-cross" />
                </IconButton>
            </Box>

            <Box sx={{ p: 3, maxHeight: '70vh', overflowY: 'auto' }}>
                {/* ITEM NAME */}
                <Typography variant="caption" sx={{ color: '#7f8c8d', fontWeight: 800, letterSpacing: 1 }}>ITEM NAME</Typography>
                <TextField
                    fullWidth
                    variant="standard"
                    value={editData.name}
                    onChange={(e) => setEditData({...editData, name: e.target.value})}
                    InputProps={{ disableUnderline: true, sx: { fontSize: '1.8rem', fontWeight: 800, color: '#2c3e50' } }}
                    sx={{ mb: 4 }}
                />

                {/* CATEGORY & SUB-CATEGORY */}
                <Stack direction="row" spacing={2} sx={{ mb: isCustom ? 2 : 4 }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" sx={{ color: '#7f8c8d', fontWeight: 800, mb: 1, display: 'block' }}>CATEGORY</Typography>
                        <Select
                            fullWidth
                            value={editData.category || "supplies"}
                            onChange={(e) => setEditData({...editData, category: e.target.value})}
                            sx={{ borderRadius: '12px', bgcolor: '#f8f9fa', '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, fontWeight: 700 }}
                        >
                            <MenuItem value="supplies">📦 Supplies</MenuItem>
                            <MenuItem value="food">🍕 Food</MenuItem>
                        </Select>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" sx={{ color: '#7f8c8d', fontWeight: 800, mb: 1, display: 'block' }}>SUB-CATEGORY</Typography>
                        <Select
                            fullWidth
                            // If the current sub_category isn't in our dynamic list, show "Custom"
                            value={dynamicSubCats.includes(editData.sub_category) ? editData.sub_category : "Custom"}
                            onChange={(e) => {
                                const val = e.target.value;
                                // If they pick Custom, we clear the field for them to type; otherwise, set the value
                                setEditData({ ...editData, sub_category: val === "Custom" ? "" : val });
                            }}
                            sx={{ borderRadius: '12px', bgcolor: '#f8f9fa', '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, fontWeight: 700 }}
                        >
                            {dynamicSubCats.map(cat => (
                                <MenuItem key={cat} value={cat}>
                                    {capitalizeFirstLetter(cat.toLowerCase())}
                                </MenuItem>
                            ))}
                            <Divider sx={{ my: 1 }} />
                            <MenuItem value="Custom" sx={{ fontStyle: 'italic', color: '#151269', fontWeight: 700 }}>
                                + Use Custom...
                            </MenuItem>
                        </Select>
                    </Box>
                </Stack>

                {/* CUSTOM SUB-CATEGORY INPUT */}
                {isCustom && (
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="caption" sx={{ color: '#151269', fontWeight: 800, mb: 0.5, display: 'block' }}>CUSTOM NAME</Typography>
                        <TextField
                            fullWidth
                            autoFocus
                            placeholder="e.g. Electronics, Lighting..."
                            value={editData.sub_category}
                            onChange={(e) => setEditData({ ...editData, sub_category: e.target.value })}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#fff' } }}
                        />
                    </Box>
                )}

                {/* MODERN PRIVACY TOGGLE */}
                <Typography variant="caption" sx={{ color: '#7f8c8d', fontWeight: 800, mb: 1, display: 'block' }}>VISIBILITY</Typography>
                <ToggleButtonGroup
                    fullWidth
                    exclusive
                    value={editData.private}
                    onChange={(e, val) => val !== null && setEditData({...editData, private: val})}
                    sx={{ mb: 4, bgcolor: '#f1f3f5', p: 0.5, borderRadius: '14px', '& .MuiToggleButton-root': { border: 'none', borderRadius: '10px', py: 1, fontWeight: 700, textTransform: 'none', color: '#7f8c8d', '&.Mui-selected': { bgcolor: '#fff', color: '#151269', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' } } }}
                >
                    <ToggleButton value={false}>Public</ToggleButton>
                    <ToggleButton value={true}>Private</ToggleButton>
                </ToggleButtonGroup>

                {/* NOTES SECTION */}
                <Typography variant="caption" sx={{ color: '#7f8c8d', fontWeight: 800, display: 'block' }}>NOTES & INSTRUCTIONS</Typography>
                <TextField
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="Add details..."
                    value={editData.notes || ""}
                    onChange={(e) => setEditData({...editData, notes: e.target.value})}
                    sx={{ mt: 1, mb: 4, '& .MuiOutlinedInput-root': { fontSize: '14px', borderRadius: '12px' } }}
                />
            </Box>

            {/* FOOTER */}
            <Box
                sx={{
                    p: 2,
                    borderTop: '1px solid #f1f3f5',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: '#fff'
                }}
            >
                {/* Left Side: Management Actions (Delete & Release) */}
                <Stack direction="row" spacing={0.5}>
                    <IconButton
                        onClick={() => onDelete(editData.id)}
                        sx={{
                            color: '#e74c3c',
                            '&:hover': { bgcolor: 'rgba(231, 76, 60, 0.08)' }
                        }}
                    >
                        <i className="icon-trash" />
                    </IconButton>

                    {isAssigned && (
                        <Button
                            onClick={handleReleaseToBounty}
                            startIcon={<i className="icon-undo" />}
                            sx={{
                                py: 1,
                                px: 1.5,
                                borderRadius: '10px',
                                fontWeight: 700,
                                color: '#fff', // Amber/Gold color for unassigning
                                textTransform: 'none',
                                bgcolor: '#d97706',
                            }}
                        >
                            Release
                        </Button>
                    )}
                </Stack>

                {/* Right Aligned: Cancel & Save */}
                <Stack direction="row" spacing={1.5}>
                    <Button
                        onClick={onClose}
                        sx={{
                            py: 1.5,
                            px: 3,
                            borderRadius: '12px',
                            fontWeight: 800,
                            color: '#95a5a6',
                            bgcolor: '#f8f9fa',
                            textTransform: 'none',
                            '&:hover': { bgcolor: '#f1f3f5' }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => onSave(editData)}
                        disabled={!editData.name.trim() || !editData.sub_category.trim()}
                        sx={{
                            py: 1.5,
                            px: 4,
                            borderRadius: '12px',
                            fontWeight: 800,
                            bgcolor: '#151269',
                            textTransform: 'none',
                            '&:hover': { bgcolor: '#1a1780' },
                            boxShadow: '0 4px 14px rgba(21, 18, 105, 0.25)',
                            '&.Mui-disabled': { bgcolor: '#bdc3c7', color: '#fff' }
                        }}
                    >
                        Save Changes
                    </Button>
                </Stack>
            </Box>
        </Dialog>
    );
}

export default SuppliesEditItemDialog;